/** «Зарплата → Банковские реестры»: ответ ручки `/api/payroll/registries`. */
import { apiFetch, query } from "@/lib/api";
import type { Money } from "@/lib/pnl";

export type RegistryStatus = "decoded" | "undecoded" | "returned";

export const STATUS_LABEL: Record<RegistryStatus, string> = {
  decoded: "расшифрован",
  undecoded: "не расшифрован",
  returned: "возвращён",
};

export const STATUS_HINT: Record<RegistryStatus, string> = {
  decoded:
    "в CRM заведены расходы «ЗП(Банк.Выплаты)» рядом по дню, и их сумма совпала с суммой реестра банка " +
    "(CRM округляет вверх до рубля). Сверено по сумме: людей банк в выписке не пишет",
  undecoded: "набора выплат CRM на сумму реестра не нашлось — выплату не завели в CRM или завели другой суммой",
  returned: "банк вернул реестр целиком — люди по нему денег не получили",
};

export type Sum = { count: number; amount: Money };

export type RegistryMonth = {
  month: string;
  registries: Sum;
  refunds: Sum;
  decoded: Sum;
  undecoded: Sum;
  payouts: Sum;
  in_registry: Sum;
  separate: Sum;
  missing: Sum;
};

export type RegistryPayout = {
  id: number;
  crm_id: number;
  date: string;
  amount: Money;
  user_name: string | null;
  role: string | null;
  location_name: string | null;
  recipient_bank: string | null;
};

export type RegistryOperation = {
  id: number;
  date: string;
  amount: Money;
  purpose: string;
  counterparty_name: string;
  account: string;
  bank_name: string;
  entrepreneur_name: string;
  article_name: string;
};

export type RegistryRefund = { id: number; date: string; amount: Money; purpose: string };

export type Registry = {
  id: number;
  date: string;
  amount: Money;
  entrepreneur_name: string;
  bank_name: string;
  account: string;
  article_name: string;
  purpose: string;
  /** «№16» у Сбера, файл «Z_…» у ВТБ; пусто — банк номер не написал. */
  label: string;
  kind: "salary" | "vacation" | "dismissal";
  kind_name: string;
  status: RegistryStatus;
  crm_amount: Money;
  payouts: RegistryPayout[];
  refunds: RegistryRefund[];
  /** Нерасшифрованному — выплаты его дня, оставшиеся без реестра. */
  same_day: RegistryPayout[];
};

export type LoosePayout = RegistryPayout & {
  status: "separate" | "missing";
  operation: RegistryOperation | null;
};

export type RegistriesReport = {
  date_from: string;
  date_to: string;
  months: RegistryMonth[];
  total: Omit<RegistryMonth, "month">;
  registries: Registry[];
  payouts: LoosePayout[];
};

export function loadRegistries(filter: { date_from: string; date_to: string }): Promise<RegistriesReport> {
  return apiFetch<RegistriesReport>(`/api/payroll/registries${query(filter)}`);
}
